import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import LangEnum from '../../../lang/LangEnum';
import { OrderListTestIds } from '../../../utils/testIds/OrderList.testIds';
import { useTranslation } from '../../translation/TranslationContext';
import { ActionEnum } from '../actions/types/orderActions.types';
import { OrderError } from '../edit/sections/OrderEditSections.types';
import BusinessPolicyViolationDetails from '../edit/validate/conditions/businessPolicy/components/BusinessPolicyViolationDetails';
import { useBusinessPolicyViolationViews } from '../edit/validate/conditions/businessPolicy/hooks/useBusinessPolicyViolationViews';
import { StatusType } from '../order.types';
import { ordersRepository } from '../repositories/ordersRepository/orders.repository';
import useFinishOrderContractNotice from './useFinishOrderContractNotice';
import useFinishOrderModalCopy from './useFinishOrderModalCopy';

interface FinishOrderModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  orderId: string;
  orderCode?: string;
  orderStatus?: StatusType;
  action?: ActionEnum;
  customerCommercialName?: string;
  isSubmittingAction: boolean;
  shouldValidateWarnings?: boolean;
}

const FinishOrderModal = ({
  onClose,
  open,
  onConfirm,
  orderId,
  isSubmittingAction,
  orderCode,
  orderStatus,
  action,
  customerCommercialName,
  shouldValidateWarnings = true,
}: FinishOrderModalProps) => {
  const { translate } = useTranslation();
  const { shouldShowAutoContractNotice } = useFinishOrderContractNotice(orderStatus);
  const { titleLang, bodyLang, trimmedCustomerName } = useFinishOrderModalCopy(
    action,
    customerCommercialName,
  );

  const [warning, setWarning] = useState<OrderError>();
  const [isLoading, setIsLoading] = useState(true);
  const warningViolations = useBusinessPolicyViolationViews(warning ? [warning] : []);

  useEffect(() => {
    let isMounted = true;

    const getWarnings = async () => {
      if (!shouldValidateWarnings) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const response = await ordersRepository(orderId).getValidateWarnings();
      setIsLoading(false);

      if (response.ok && isMounted) {
        setWarning(response.data?.value);
      }
    };

    if (open) {
      getWarnings();
    }

    return () => {
      isMounted = false;
    };
  }, [orderId, open, shouldValidateWarnings]);

  const getPrimaryButtonLangEnum = () => {
    if (warning) {
      return LangEnum.ORDER_ITEMS_FINISH_MESSAGE;
    }
    if (orderStatus === StatusType.ON_APPROVAL) {
      return LangEnum.GLOBAL_APPROVE;
    }
    return LangEnum.ORDER_SUMMARY_FINISH_ORDER_ACTION_LABEL;
  };

  return (
    <Modal
      isOpen={open}
      size={warningViolations.length ? 'expanded' : 'standard'}
      header={{ title: translate(titleLang, { orderCode }) }}
      onClose={onClose}
      footer={{
        primaryAction: {
          label: translate(getPrimaryButtonLangEnum()),
          onClick: onConfirm,
          isDisabled: isSubmittingAction,
          isLoading,
          testId: OrderListTestIds.FINALIZE_CONFIRM_BUTTON,
        },
        secondaryAction: {
          label: translate(LangEnum.GLOBAL_CANCEL_LABEL),
          onClick: onClose,
        },
      }}
    >
      <Grid container spacing={2}>
        {bodyLang && (
          <Grid size={12}>
            <Typography variant="body1" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
              {translate(bodyLang, { customerCommercialName: trimmedCustomerName })}
            </Typography>
          </Grid>
        )}
        {shouldShowAutoContractNotice && (
          <Grid size={12}>
            <Typography variant="body1" color="textSecondary">
              {translate(LangEnum.ORDER_APPROVE_CONTRACT_MESSAGE)}
            </Typography>
          </Grid>
        )}
        {!!warningViolations.length && (
          <Grid size={12}>
            <BusinessPolicyViolationDetails violations={warningViolations} />
          </Grid>
        )}
      </Grid>
    </Modal>
  );
};

export default FinishOrderModal;
